import React, { useRef, useEffect, useState } from 'react';
import { Icon } from './ui';

export type QuickCreateKind = 'customer' | 'lead' | 'quote' | 'project';

interface Props {
  onPick: (kind: QuickCreateKind) => void;
}

const ITEMS: { kind: QuickCreateKind; label: string; hint: string; icon: string; tone: string }[] = [
  { kind: 'quote',    label: 'הצעת מחיר', hint: 'עבודה קצרה, בלי פרויקט', icon: 'description', tone: 'text-indigo-600 bg-indigo-50' },
  { kind: 'project',  label: 'פרויקט',    hint: 'הזדמנות לניהול לאורך זמן', icon: 'folder_open', tone: 'text-amber-600 bg-amber-50' },
  { kind: 'customer', label: 'לקוח',      hint: 'לקוח משלם',               icon: 'person_add',  tone: 'text-emerald-600 bg-emerald-50' },
  { kind: 'lead',     label: 'ליד',       hint: 'פנייה שטרם הבשילה',        icon: 'person_search', tone: 'text-sky-600 bg-sky-50' },
];

/**
 * Create anything from the header, without first navigating to the right page.
 *
 * Quote is listed first and is the common case — the same ordering the rest of
 * the app uses, so the default never depends on which screen you came from.
 */
export const QuickCreateMenu: React.FC<Props> = ({ onPick }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative flex-shrink-0">
      <button
        onClick={() => setOpen((v) => !v)}
        className="size-10 rounded-lg bg-primary text-white hover:bg-primary/90 flex items-center justify-center transition-colors"
        aria-label="יצירה מהירה"
        aria-haspopup="menu"
        aria-expanded={open}
        title="יצירה מהירה"
      >
        <Icon name="add" size={20} />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute top-full end-0 mt-1.5 w-64 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl z-50 overflow-hidden py-1"
        >
          {ITEMS.map((it) => (
            <button
              key={it.kind}
              role="menuitem"
              type="button"
              onClick={() => { setOpen(false); onPick(it.kind); }}
              className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 dark:hover:bg-slate-800 text-start transition-colors"
            >
              <span className={`size-8 rounded-lg flex items-center justify-center flex-shrink-0 ${it.tone}`}>
                <Icon name={it.icon} size={17} />
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-slate-900 dark:text-white">{it.label}</span>
                <span className="block text-[11px] text-slate-500 truncate">{it.hint}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default QuickCreateMenu;
